import "./ChatCardToolbar.css";
import "@ui/Dropdown.css";
import { Button } from "@ui/Button";
import { useSelectOnlyCombobox } from "@ui/hooks/useSelectOnlyCombobox";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import { toast } from "@ui/molecules/Toasts/toast";
import { Stack } from "@ui/Stack";
import { Tooltip, TooltipTrigger } from "@ui/Tooltip";
import {
	createContext,
	createSignal,
	type JSX,
	onCleanup,
	onMount,
	type ParentComponent,
	Show,
	useContext,
} from "solid-js";
import { Portal } from "solid-js/web";
import { patchInteraction } from "@/client";
import type { ArtifactOutput } from "../../ChatEventTypes.ts";
import { ArtifactDownload } from "./ArtifactDownload";
import { AssetState } from "./AssetState";
import { RatingReasonCard } from "./RatingReasonCard";

declare const pageVars: {
	appEmbedded: boolean;
};

export type ChatCardToolbarProps = {
	questionId: string;
	interactionId: string;
	/**
	 * How the rated artifact is named when asking what was wrong with it,
	 * determiner included because clips are several ("these clips"). Rating
	 * controls are hidden when omitted.
	 */
	ratingSubject?: string;
	cardType: string;
	cardTypeSymbol: string;
	cardTypeIconColour?: string;
	/**
	 * Artifact this toolbar saves and rates, if one applies. Ratings are stored
	 * on the artifact, so rating controls are hidden without it.
	 */
	artifactId?: string;
	/** `false` hides Save for artifact types that can't be saved. */
	saveable?: boolean;
	documentId?: string;
	isFavourite?: boolean;
	/** Downloadable files from `artifact.outputs` (URL + format). */
	outputs?: ArtifactOutput[];
	/** A page the artifact lives on, linked just before the rating controls. */
	viewLink?: { label: string; href: string };
	/** Separate the toolbar from content above it (speaking / card footers). */
	padTop?: boolean;
	onCopy?: () => void;
};

export type ChatCardToolbarRatingEventDetail = {
	artifactId: string;
	rating: -1 | 1;
	/**
	 * The reason now stored against the rating. Omitted means "unchanged", which
	 * matches the API: a rating sent without one leaves any stored reason alone.
	 */
	reason?: string | null;
};

export type ChatCardToolbarActions = {
	/** Saving the card's artifact to the question's answers. */
	save: boolean;
	/** Thumbs up / down on the card's artifact. */
	rate: boolean;
};

const ChatCardToolbarActionsContext = createContext<ChatCardToolbarActions>({
	save: true,
	rate: true,
});

/**
 * Sets which owner actions the card toolbars inside it offer. Both are on
 * unless turned off, e.g. for a reader who doesn't own the question.
 */
export const ChatCardToolbarActionsProvider: ParentComponent<
	Partial<ChatCardToolbarActions>
> = (props) => (
	<ChatCardToolbarActionsContext.Provider
		value={{
			get save() {
				return props.save ?? true;
			},
			get rate() {
				return props.rate ?? true;
			},
		}}
	>
		{props.children}
	</ChatCardToolbarActionsContext.Provider>
);

export const useChatCardToolbarActions = () =>
	useContext(ChatCardToolbarActionsContext);

const chatCardToolbarRatingEvent = "card-toolbar:rating-updated";

/*
 * What has been announced so far, kept because the announcements are the only
 * time a rating is heard: a question's stored ratings are sent round once as it
 * loads. A toolbar mounted after that - the pane's copy of a document card's,
 * which exists only while the reader has the document open - reads its opening
 * state from here instead of starting out unrated.
 */
const announcedRatings = new Map<
	string,
	{ rating: -1 | 1; reason?: string | null }
>();

export const emitChatCardToolbarRatingUpdated = (
	detail: ChatCardToolbarRatingEventDetail,
) => {
	announcedRatings.set(detail.artifactId, {
		rating: detail.rating,
		// Omitted leaves any stored reason alone, here as at the API.
		reason:
			detail.reason === undefined
				? announcedRatings.get(detail.artifactId)?.reason
				: detail.reason,
	});
	document.dispatchEvent(
		new CustomEvent<ChatCardToolbarRatingEventDetail>(
			chatCardToolbarRatingEvent,
			{ detail },
		),
	);
};

export const ChatCardToolbar = (props: ChatCardToolbarProps) => {
	const actions = useChatCardToolbarActions();
	const announced =
		props.artifactId === undefined
			? undefined
			: announcedRatings.get(props.artifactId);
	const [isSubmittingRating, setIsSubmittingRating] = createSignal(false);
	const [currentRating, setCurrentRating] = createSignal<-1 | 1 | null>(
		announced?.rating ?? null,
	);
	const [isRatingPanelOpen, setIsRatingPanelOpen] = createSignal(false);
	const [isPanelInteractionBlocked, setIsPanelInteractionBlocked] =
		createSignal(false);
	const [isReasonCardOpen, setIsReasonCardOpen] = createSignal(false);
	const [isSubmittingReason, setIsSubmittingReason] = createSignal(false);
	const reasonCombobox = useSelectOnlyCombobox({
		// Do not tie `disabled` to `isSubmittingRating`: opening the panel submits
		// the rating, and the hook closes whenever `disabled` becomes true.
		disabled: () => false,
		mode: "panel",
		optionCount: () => 0,
		selectedIndex: () => -1,
		getOptionLabel: () => "",
		onSelect: () => {},
		placement: () => "top end",
	});
	const [currentReason, setCurrentReason] = createSignal(
		announced?.reason ?? "",
	);
	let appEmbeddedRatingRef: HTMLDivElement | undefined;
	let ratingPanelRef: HTMLDivElement | undefined;
	let unblockPanelInteractionTimeoutId: number | undefined;

	onMount(() => {
		/*
		 * The same artifact can carry two of these at once - a document's card and
		 * the pane it opens in - so a rating given to either is announced, and the
		 * other follows it.
		 */
		const handleRatingUpdated = (event: Event) => {
			const customEvent =
				event as CustomEvent<ChatCardToolbarRatingEventDetail>;
			if (customEvent.detail.artifactId !== props.artifactId) {
				return;
			}
			setCurrentRating(customEvent.detail.rating);
			if (customEvent.detail.reason !== undefined) {
				setCurrentReason(customEvent.detail.reason ?? "");
			}
		};
		const handleDocumentMouseDown = (event: MouseEvent) => {
			if (!isRatingPanelOpen()) {
				return;
			}
			const target = event.target as Node | null;
			if (
				target &&
				appEmbeddedRatingRef &&
				!appEmbeddedRatingRef.contains(target)
			) {
				setIsRatingPanelOpen(false);
			}
		};
		const handleEscape = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				setIsRatingPanelOpen(false);
				if (!isSubmittingReason()) {
					setIsReasonCardOpen(false);
				}
			}
		};
		document.addEventListener(chatCardToolbarRatingEvent, handleRatingUpdated);
		document.addEventListener("mousedown", handleDocumentMouseDown);
		document.addEventListener("keydown", handleEscape);
		onCleanup(() => {
			if (unblockPanelInteractionTimeoutId !== undefined) {
				window.clearTimeout(unblockPanelInteractionTimeoutId);
			}
			document.removeEventListener(
				chatCardToolbarRatingEvent,
				handleRatingUpdated,
			);
			document.removeEventListener("mousedown", handleDocumentMouseDown);
			document.removeEventListener("keydown", handleEscape);
		});
	});

	/**
	 * `reason` follows the API's three states: omitted leaves any stored reason
	 * alone, a string replaces it, and `null` clears it.
	 */
	const submitRating = async (rating: -1 | 1, reason?: string | null) => {
		const artifactId = props.artifactId;
		if (!props.questionId || !props.interactionId || !artifactId) {
			return false;
		}
		setIsSubmittingRating(true);
		try {
			const response = await patchInteraction({
				path: {
					question_id: props.questionId,
					interaction_id: props.interactionId,
				},
				body: {
					artifact_id: artifactId,
					rating,
					...(reason === undefined ? {} : { reason }),
				},
			});
			if (response.error) {
				toast.error({ title: "Failed to submit rating" });
				return false;
			}
			emitChatCardToolbarRatingUpdated({
				artifactId,
				rating,
				reason,
			});
			return true;
		} catch (_error) {
			toast.error({ title: "Failed to submit rating" });
			return false;
		} finally {
			setIsSubmittingRating(false);
		}
	};

	/**
	 * A negative rating is stored before the reason is asked for, so it survives
	 * the user dismissing the prompt; the reason is a second, fuller submission.
	 * A reason belongs to the rating it was given with, so changing the rating in
	 * either direction clears it, while re-affirming the same rating keeps it.
	 */
	const rateInteraction = async (rating: -1 | 1) => {
		if (isSubmittingRating()) {
			return;
		}
		const changesRating = rating !== currentRating();
		if (rating === -1 && pageVars.appEmbedded) {
			setIsReasonCardOpen(true);
		}
		const submitted = await submitRating(
			rating,
			changesRating ? null : undefined,
		);
		if (!submitted) {
			if (rating === -1) {
				setIsReasonCardOpen(false);
				reasonCombobox.close();
			}
			return;
		}
		if (rating === 1) {
			toast.success({ title: "Thanks for your feedback" });
		}
	};

	const submitRatingReason = async (reason: string) => {
		setIsSubmittingReason(true);
		const submitted = await submitRating(-1, reason);
		setIsSubmittingReason(false);
		if (submitted) {
			setIsReasonCardOpen(false);
			reasonCombobox.close();
			toast.success({ title: "Thanks for your feedback" });
		}
	};

	const handleReasonTriggerClick: JSX.EventHandlerUnion<
		HTMLButtonElement,
		MouseEvent
	> = (event) => {
		const opening = !reasonCombobox.isOpen();
		reasonCombobox.onTriggerClick(event);
		if (opening) {
			void rateInteraction(-1);
		}
	};

	const submitRatingFromPanel = (rating: -1 | 1) => {
		setIsRatingPanelOpen(false);
		void rateInteraction(rating);
	};

	const revealRatingPanel = () => {
		if (!ratingPanelRef) {
			return;
		}
		ratingPanelRef.scrollIntoView({ block: "nearest", inline: "nearest" });
	};

	const blockPanelInteraction = () => {
		setIsPanelInteractionBlocked(true);
		if (unblockPanelInteractionTimeoutId !== undefined) {
			window.clearTimeout(unblockPanelInteractionTimeoutId);
		}
		unblockPanelInteractionTimeoutId = window.setTimeout(() => {
			setIsPanelInteractionBlocked(false);
		}, 250);
	};

	const handleTriggerPointerDown = (event: PointerEvent) => {
		if (event.pointerType === "touch") {
			blockPanelInteraction();
		}
	};

	const openRatingPanel = () => {
		setIsRatingPanelOpen(true);
		requestAnimationFrame(revealRatingPanel);
	};

	const toggleRatingPanel = () => {
		if (isRatingPanelOpen()) {
			setIsRatingPanelOpen(false);
			return;
		}
		openRatingPanel();
	};

	const subject = () => props.ratingSubject ?? "";

	const unhelpfulLabel = () => `Let us know ${subject()} was unhelpful`;

	return (
		<Stack
			class="card-toolbar"
			classList={{ "card-toolbar-pad-top": Boolean(props.padTop) }}
			direction="row"
			spacing="large"
			justify="space-between"
			align="center"
		>
			<Stack class="card-type" direction="row" spacing="medium" align="center">
				<MaterialSymbol
					symbol={props.cardTypeSymbol}
					relativeSize="even-larger"
					style={
						props.cardTypeIconColour
							? { color: props.cardTypeIconColour }
							: undefined
					}
				/>
				<span
					class="card-type-label"
					style={{ color: props.cardTypeIconColour }}
				>
					{props.cardType}
				</span>
			</Stack>
			<Stack
				class="card-toolbar-right"
				direction="row"
				spacing="none"
				align="center"
			>
				<Show
					when={
						props.outputs && props.outputs.length > 0
							? props.outputs
							: undefined
					}
				>
					{(outputs) => <ArtifactDownload outputs={outputs()} />}
				</Show>
				<Show
					when={
						actions.save && props.saveable !== false
							? props.artifactId
							: undefined
					}
				>
					{(artifactId) => (
						<AssetState
							artifact_id={artifactId()}
							document_id={props.documentId}
							is_favourite={props.isFavourite}
							question_id={props.questionId}
						/>
					)}
				</Show>
				<Show when={props.viewLink}>
					{(link) => (
						<Button
							as="a"
							href={link().href}
							monospaced
							variant="text"
							theme="monochrome"
						>
							{link().label}
						</Button>
					)}
				</Show>
				<Show when={actions.rate && props.artifactId && props.ratingSubject}>
					<Show when={pageVars.appEmbedded}>
						<div class="app-embedded-rating" ref={appEmbeddedRatingRef}>
							<Button
								symbol={
									currentRating() === 1
										? "thumb_up"
										: currentRating() === -1
											? "thumb_down"
											: "thumbs_up_down"
								}
								symbolFilled={currentRating() !== null}
								symbolRelativeSize="even-larger"
								variant="text"
								theme="monochrome"
								shape="circle"
								touchTarget="compact"
								class={`rating-button app-embedded-rating-trigger${currentRating() !== null ? " is-rated" : ""}`}
								disabled={isSubmittingRating()}
								onPointerDown={handleTriggerPointerDown}
								onClick={toggleRatingPanel}
							/>
							<Show when={isRatingPanelOpen()}>
								<div
									class="rating-panel"
									role="menu"
									aria-label="Rate response"
									ref={ratingPanelRef}
								>
									<Button
										symbol="thumb_up"
										aria-label="Helpful answer"
										symbolFilled={currentRating() === 1}
										symbolRelativeSize="even-larger"
										variant="text"
										theme="monochrome"
										shape="circle"
										touchTarget="compact"
										class={`rating-button rating-popup-button${currentRating() === 1 ? " selected" : ""}`}
										disabled={
											isSubmittingRating() || isPanelInteractionBlocked()
										}
										data-rating="1"
										data-interaction-id={props.interactionId}
										onClick={() => submitRatingFromPanel(1)}
									/>
									<Button
										symbol="thumb_down"
										aria-label="Unhelpful answer"
										symbolFilled={currentRating() === -1}
										symbolRelativeSize="even-larger"
										variant="text"
										theme="monochrome"
										shape="circle"
										touchTarget="compact"
										class={`rating-button rating-popup-button${currentRating() === -1 ? " selected" : ""}`}
										disabled={
											isSubmittingRating() || isPanelInteractionBlocked()
										}
										data-rating="-1"
										data-interaction-id={props.interactionId}
										onClick={() => submitRatingFromPanel(-1)}
									/>
								</div>
							</Show>
						</div>
					</Show>
					<Show when={!pageVars.appEmbedded}>
						<TooltipTrigger>
							<Button
								symbol="thumb_up"
								aria-label={`Let us know ${subject()} was helpful`}
								symbolFilled={currentRating() === 1}
								symbolRelativeSize="even-larger"
								variant="text"
								theme="monochrome"
								shape="circle"
								touchTarget="compact"
								class={`rating-button${currentRating() === 1 ? " selected" : ""}`}
								disabled={isSubmittingRating()}
								data-rating="1"
								data-interaction-id={props.interactionId}
								onClick={() => rateInteraction(1)}
							/>
							<Tooltip>Let us know {subject()} was helpful</Tooltip>
						</TooltipTrigger>
						<div
							ref={reasonCombobox.setRootEl}
							class="gvtui-dropdown gvtui-dropdown--has-trigger rating-reason-combobox"
							classList={{
								"gvtui-dropdown--open": reasonCombobox.isOpen(),
							}}
						>
							<TooltipTrigger disabled={reasonCombobox.isOpen()}>
								<Button
									ref={reasonCombobox.setTriggerEl}
									symbol="thumb_down"
									aria-label={unhelpfulLabel()}
									symbolFilled={currentRating() === -1}
									symbolRelativeSize="even-larger"
									variant="text"
									theme="monochrome"
									shape="circle"
									touchTarget="compact"
									class={`rating-button${currentRating() === -1 ? " selected" : ""}`}
									disabled={isSubmittingRating()}
									data-rating="-1"
									data-interaction-id={props.interactionId}
									{...reasonCombobox.triggerProps()}
									onClick={handleReasonTriggerClick}
									onKeyDown={reasonCombobox.onTriggerKeyDown}
								/>
								<Tooltip>
									{currentReason() === "" ? unhelpfulLabel() : currentReason()}
								</Tooltip>
							</TooltipTrigger>
							<Show when={reasonCombobox.isOpen()}>
								<Portal>
									<div
										ref={reasonCombobox.setListboxEl}
										role="listbox"
										id={reasonCombobox.listboxId}
										class="gvtui-dropdown__listbox gvtui-dropdown__listbox--panel rating-reason-combobox-panel"
										aria-label={`What was wrong with ${subject()}?`}
										data-placement={
											reasonCombobox.popupPosition()?.placement ?? "top end"
										}
										style={{
											width: "min(24rem, calc(100vw - 2rem))",
											top: reasonCombobox.popupPosition()?.top ?? "0px",
											left: reasonCombobox.popupPosition()?.left ?? "0px",
											"min-width":
												reasonCombobox.popupPosition()?.minWidth ?? "100px",
										}}
									>
										<RatingReasonCard
											subject={subject()}
											reason={currentReason()}
											isSubmitting={isSubmittingReason()}
											onSubmit={(reason) => void submitRatingReason(reason)}
											onDismiss={() => reasonCombobox.close()}
										/>
									</div>
								</Portal>
							</Show>
						</div>
					</Show>
				</Show>
				<Show when={props.onCopy}>
					<TooltipTrigger>
						<Button
							symbol="content_copy"
							aria-label="Copy answer"
							symbolRelativeSize="even-larger"
							variant="text"
							theme="monochrome"
							shape="circle"
							touchTarget="compact"
							onClick={() => props.onCopy?.()}
						/>
						<Tooltip>Copy answer</Tooltip>
					</TooltipTrigger>
				</Show>
			</Stack>
			<Show when={pageVars.appEmbedded && isReasonCardOpen()}>
				<div class="rating-reason-card-embed">
					<RatingReasonCard
						subject={subject()}
						reason={currentReason()}
						isSubmitting={isSubmittingReason()}
						onSubmit={(reason) => void submitRatingReason(reason)}
						onDismiss={() => setIsReasonCardOpen(false)}
					/>
				</div>
			</Show>
		</Stack>
	);
};
